import type {
  ContentFlagAttributes,
  SessionCommentAttributes,
  UserAttributes,
} from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import type { ContentFlagStatus, ContentFlagType } from '@/client/services/contentFlag'
import type { ModerationFlagCategory, ModerationFlagSeverity } from '@/client/services/moderation'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import UserQ from './UserQ'

const ContentFlagQ = {
  ...makeQueryModelV2<ContentFlagAttributes>('content_flag')([
    'id',
    'content_type',
    'category',
    'content',
    'link',
    'status',
    'severity',
    'user_id',
    'details',
    'reviewer_id',
    'reviewed_at',
    'updated_at',
    'created_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    User: { query: UserQ, foreignKey: UserQ.id, sourceKey: ContentFlagQ.user_id },
  }),

  getUser: (): SQLFragment<UserAttributes> =>
    SQL`${UserQ.where({
      id: ContentFlagQ.user_id,
    })}`,

  getReviewer: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: ContentFlagQ.reviewer_id,
    }),

  where: (
    {
      id,
      userId,
      reviewerId,
      contentType,
      severity,
      include = [],
      paranoid = true,
      groupBy = null,
      order = null,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      reviewerId?: number | null | SQLFragment<number>
      contentType?: ContentFlagType | null
      severity?: ModerationFlagSeverity | null
      include?: IncludeAssociations
      paranoid?: boolean
      groupBy?: GroupBy | null
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SessionCommentAttributes> => SQL`
        FROM ${ContentFlagQ}
        ${includeAssociationsToSql(ContentFlagQ, include)}
        WHERE
            ${paranoid ? SQL`${ContentFlagQ.deleted_at} IS NULL AND` : SQL``}
            ${id != null ? SQL`${ContentFlagQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${ContentFlagQ.user_id} = ${userId} AND` : SQL``}
            ${reviewerId != null ? SQL`${ContentFlagQ.reviewer_id} = ${reviewerId} AND` : SQL``}
            ${contentType != null ? SQL`${ContentFlagQ.content_type} = ${contentType} AND` : SQL``}
            ${severity != null ? SQL`${ContentFlagQ.severity} = ${severity} AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  hasSeverity: (severity: ModerationFlagSeverity): SQLFragment<boolean> =>
    SQL`${ContentFlagQ.severity} = ${severity}`,

  hasCategory: (category: ModerationFlagCategory): SQLFragment<boolean> =>
    SQL`${ContentFlagQ.category} = ${category}`,

  hasType: (type: ContentFlagType): SQLFragment<boolean> =>
    SQL`${ContentFlagQ.content_type} = ${type}`,

  hasStatus: (status: ContentFlagStatus): SQLFragment<boolean> =>
    SQL`${ContentFlagQ.status} = ${status}`,
}

export default ContentFlagQ
